import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { BannerStatus } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification, notification } from "infrastructure/notification";
import { getScreenExtraReducers } from "ui/app";
import { ITerm } from "ui/screens/plex-dictionary/term-translations";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { getDictionaryLinksInitialState, IDictionaryLinksState, IAssociatedMessage } from "./DictionaryLinks.state";

const initialState = getDictionaryLinksInitialState();

export const dictionaryLinks = createSlice({
  initialState: initialState,
  name: "dictionaryLinks",
  reducers: {
    initializeDictionaryLinks: (state: IDictionaryLinksState, _: PayloadAction<string>) => {
      return { ...state, isLoading: true };
    },
    initializeDictionaryLinksCompleted: (state: IDictionaryLinksState, action: PayloadAction<number>) => {
      return { ...state, isLoading: false, messageVersion: action.payload, newAssociatedTerm: [], toDeleteAssociatedTerm: [], notifications: [] };
    },
    initializeDictionaryLinksFailed: (state: IDictionaryLinksState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, notifications: [action.payload], newAssociatedTerm: [], toDeleteAssociatedTerm: [] };
    },
    loadPickerTerms: (state: IDictionaryLinksState, _: PayloadAction<IPickerSearchCriterion<ITerm>>) => {
      return { ...state, isLoading: true };
    },
    loadPickerTermsCompleted: (state: IDictionaryLinksState, action: PayloadAction<ITerm[]>) => {
      return { ...state, isLoading: false, termsPickerData: action.payload };
    },
    loadPickerTermsFailed: (state: IDictionaryLinksState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, termsPickerData: [], notifications: [action.payload] };
    },
    loadAssociatedTerms: (state: IDictionaryLinksState, _: PayloadAction<string>) => {
      return { ...state, isLoading: true };
    },
    loadAssociatedTermsCompleted: (state: IDictionaryLinksState, action: PayloadAction<ITerm[]>) => {
      return { ...state, isLoading: false, associatedTerms: action.payload };
    },
    loadAssociatedTermsFailed: (state: IDictionaryLinksState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, associatedTerms: [], notifications: [action.payload] };
    },
    addAssociatedTerm: (state: IDictionaryLinksState, _: PayloadAction<{ message: IAssociatedMessage; redirect?: boolean }>) => {
      return { ...state, isLoading: true };
    },
    addAssociatedTermCompleted: (state: IDictionaryLinksState, action: PayloadAction<{ term: ITerm; message: INotification; redirect?: boolean }>) => {
      return {
        ...state,
        isLoading: false,
        shouldRedirect: action.payload.redirect,
        newAssociatedTerm: [],
        associatedTerms: [...state.associatedTerms, action.payload.term],
        notifications: [action.payload.message]
      };
    },
    addAssociatedTermFailed: (state: IDictionaryLinksState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    deleteAssociatedTerm: (state: IDictionaryLinksState, _: PayloadAction<IAssociatedMessage>) => {
      return { ...state, isLoading: true };
    },
    deleteAssociatedTermCompleted: (state: IDictionaryLinksState, action: PayloadAction<{ removedTerm: ITerm; message: INotification }>) => {
      const terms: ITerm[] = state.associatedTerms.filter(t => t.termId !== action.payload.removedTerm.termId);
      return {
        ...state,
        isLoading: false,
        termsPickerData: state.termsPickerData,
        newAssociatedTerm: state.newAssociatedTerm,
        associatedTerms: terms,
        toDeleteAssociatedTerm: [],
        notifications: [action.payload.message]
      };
    },
    deleteAssociatedTermFailed: (state: IDictionaryLinksState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, toDeleteAssociatedTerm: [], notifications: [action.payload] };
    },
    selectNewTerm: (state: IDictionaryLinksState, action: PayloadAction<ITerm[]>) => {
      return { ...state, newAssociatedTerm: action.payload };
    },
    selectAssociatedTerm: (state: IDictionaryLinksState, action: PayloadAction<ITerm[]>) => {
      return { ...state, toDeleteAssociatedTerm: action.payload };
    },
    cancelLinkClick: (state: IDictionaryLinksState) => {
      return { ...state, isLoading: true, shouldRedirect: true };
    },
    setValidationBanner: (state: IDictionaryLinksState) => {
      return { ...state, notifications: [notification.warning().useMessageId(validationMessages.error("summary"))] };
    },
    clearValidationNotification: (state: IDictionaryLinksState) => {
      return { ...state, notifications: [...state.notifications.filter(n => n.type !== BannerStatus.warning)] };
    }
  },
  extraReducers: getScreenExtraReducers<IDictionaryLinksState>("notifications", initialState)
});
